import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Stack,
  Text,
  VStack,
  Image,
  Icon,
  SimpleGrid,
  Link,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import SEO from "../../SEO";
import { LandingContent } from "./types";
import { mainColor } from "../mainPage/MainPage";
import { FaCalendarCheck } from "react-icons/fa";
import { keyframes } from "@emotion/react";

const MotionBox = motion(Box);

interface DynamicLandingProps {
  content: LandingContent;
}

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const DynamicLanding: React.FC<DynamicLandingProps> = ({ content }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  return (
    <>
      <SEO
        title={content.seo.title}
        description={content.seo.description}
        ogTitle={content.seo.ogTitle}
        ogDescription={content.seo.ogDescription}
        keywords={content.seo.keywords}
        ogImage={content.seo.image}
      />

      <Box
        bgGradient="linear(to-b, white, gray.50)"
        minH="100vh"
        py={{ base: 12, md: 20 }}
        px={4}
      >
        <Container maxW="6xl">
          <Flex direction="column" align="center" textAlign="center" mb={20}>
            <Heading
              as="h1"
              mb={8}
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="bold"
              bgGradient="linear(to-r, teal.300, blue.500)"
              bgClip="text"
            >
              {content.header.heading}
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              maxW="3xl"
            >
              {content.header.subheading}
            </Text>
            <Text
              fontSize={{ base: "xl", md: "3xl" }}
              color={mainColor}
              maxW="3xl"
              fontStyle="italic"
              fontWeight={600}
              mt={4}
            >
              {content.header.highlightText}
            </Text>
          </Flex>

          <MotionBox
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true }}
            mb={24}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={12}
              alignItems="center"
              mb={24}
            >
              <VStack align="start" spacing={5}>
                <Heading fontSize="2xl">{content.problems.title}</Heading>
                <Stack spacing={3} color="gray.600">
                  {content.problems.features.map(
                    ({ icon: IconComp, text }, i) => (
                      <Text key={i}>
                        <Icon as={IconComp} color={mainColor} mr={2} />
                        {text}
                      </Text>
                    )
                  )}
                </Stack>
                <Text fontWeight="medium" color="gray.700" pt={2}>
                  {content.problems.summaryText}
                </Text>
                <Button
                  as="a"
                  href={content.problems.cta.href}
                  colorScheme="teal"
                  size="lg"
                  rounded="full"
                  px={8}
                  py={6}
                  fontWeight="bold"
                  target="_blank"
                  rel="noopener noreferrer"
                  _hover={{
                    transform: "scale(1.05)",
                    boxShadow: "dark-xl",
                    textDecoration: "none",
                    rel: "noopener noreferrer",
                  }}
                >
                  {content.problems.cta.label}
                </Button>
              </VStack>
              <Image
                src={content.problems.imageSrc}
                alt={content.problems.imageAlt}
                maxW="100%"
                rounded={10}
              />
            </SimpleGrid>
          </MotionBox>

          <Box
            bg="gray.100"
            p={{ base: 10, md: 14 }}
            rounded="2xl"
            shadow="md"
            w="full"
            maxW="4xl"
            textAlign="left"
            mx="auto"
            mb={16}
          >
            <Heading fontSize={{ base: "2xl", md: "3xl" }} mb={4}>
              {content.whyDigitalize.title}
            </Heading>
            <Stack spacing={4} fontSize="md">
              {content.whyDigitalize.features.map(
                ({ icon: IconComp, text }, i) => (
                  <Text key={i}>
                    <Icon as={IconComp} color={mainColor} mr={3} />
                    {text}
                  </Text>
                )
              )}
            </Stack>
          </Box>

          <MotionBox
            p={{ base: 6, md: 10 }}
            w="full"
            maxW="6xl"
            mx="auto"
            mb={16}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <Image
                src={content.whyTheyLove.imageSrc}
                alt={content.whyTheyLove.imageAlt}
                rounded="2xl"
                w="full"
                objectFit="cover"
              />

              <Box textAlign="left">
                <Text as="h3" fontSize="2xl" fontWeight="bold" mb={4}>
                  {content.whyTheyLove.title}
                </Text>

                <Stack spacing={4} color="gray.700">
                  {content.whyTheyLove.features.map(
                    ({ icon: IconComp, text }, i) => (
                      <Flex align="center" gap={3}>
                        <Icon as={IconComp} color={mainColor} />
                        <Text key={i}>{text}</Text>
                      </Flex>
                    )
                  )}
                </Stack>
              </Box>
            </SimpleGrid>
          </MotionBox>

          <Box
            bgGradient="linear(to-r, teal.600, teal.400)"
            p={{ base: 10, md: 14 }}
            rounded="2xl"
            color="white"
            textAlign="center"
            boxShadow="2xl"
            transition="transform 0.3s ease"
            _hover={{ transform: "scale(1.02)" }}
            mb={20}
          >
            <Text as="h3" fontSize="2xl" fontWeight="bold" mb={3} color="white">
              {content.trial.title}
            </Text>
            <Text as="h3" fontSize="2xl" fontWeight="bold" mb={3} color="white">
              {content.trial.title2}
            </Text>
            <Text fontSize={{ base: "lg", md: "xl" }} mb={8} color="white">
              {content.trial.subtitle}
            </Text>
            <Button
              as="a"
              href="https://app.wobble.hu/signup_page"
              colorScheme="blue"
              size="lg"
              rounded="full"
              px={8}
              py={6}
              fontWeight="bold"
              target="_blank"
              rel="noopener noreferrer"
              _hover={{
                transform: "scale(1.05)",
                boxShadow: "dark-xl",
                textDecoration: "none",
                rel: "noopener noreferrer",
              }}
            >
              {content.trial.cta.label}
            </Button>
          </Box>
          <Box
            bgGradient="linear(to-br, teal.400, teal.600)"
            p={{ base: 8, md: 12 }}
            borderRadius="3xl"
            boxShadow="2xl"
            maxW="lg"
            mx="auto"
            textAlign="center"
            color="white"
            animation={
              prefersReducedMotion
                ? undefined
                : `${float} 4s ease-in-out infinite`
            }
          >
            <VStack spacing={6}>
              <Text fontSize={{ base: "lg", md: "xl" }} maxW="md" color="white">
                Ha nem vagy biztos benne, hogy neked való, <br />
                gyere és megmutatom, mit tud a rendszer.
              </Text>

              <Icon
                as={FaCalendarCheck}
                boxSize={12}
                color="white"
              />

              <Button
                as={Link}
                href="https://book.wobble.hu"
                size="lg"
                bg="white"
                color="teal.600"
                fontWeight="bold"
                rounded="full"
                _hover={{
                  color: "teal.700",
                  transform: "scale(1.05)",
                  textDecoration: "none",
                  rel: "noopener noreferrer",
                }}
                target="_blank"
                rel="noopener noreferrer"
                _active={{ transform: "scale(0.95)" }}
                transition="all 0.2s ease-in-out"
                animation={
                  prefersReducedMotion
                    ? undefined
                    : `${pulse} 2s ease-in-out infinite`
                }
              >
                Érdekel a bemutató
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default DynamicLanding;
